import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Pagination
} from '@mui/material';
import { FaChevronDown, FaChevronUp, FaHistory } from 'react-icons/fa';
import styled from 'styled-components';
import { employeesApi } from '../../../../../../../services/api';
import { EmployeeAvatar } from '../../../../../../common/EmployeeAvatar';
import LoadingSpinner from '../../../../../../common/LoadingSpinner';
import EmptyState from '../../../../../../common/EmptyState';
import { MOR_SOURCE_LABELS, MOR_SOURCE_COLORS } from '../../../../../../../constants/managerHistory';
import { formatDateOnly } from '../../../../../../../utils/dateOnly';

const HistoryContainer = styled.div`
  margin-top: 16px;
  border-top: 1px solid #E5E7EB;
  padding-top: 16px;
`;

const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 0;

  &:hover {
    opacity: 0.8;
  }
`;

const HistoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  font-weight: 500;
  font-size: 14px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 12px;
`;

const HistoryItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #F3F4F6;

  &:last-child {
    border-bottom: none;
  }
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
  min-width: 16px;
`;

const TimelineDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$isCurrent ? '#10B981' : '#8B5CF6'};
  border: 2px solid ${props => props.$isCurrent ? '#D1FAE5' : '#EDE9FE'};
  flex-shrink: 0;
`;

const TimelineLine = styled.div`
  width: 2px;
  flex-grow: 1;
  min-height: 30px;
  background-color: #E5E7EB;
  margin-top: 4px;
`;

const ManagerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const ManagerDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const ManagerName = styled.div`
  font-weight: 500;
  color: #1F2937;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateRange = styled.div`
  font-size: 12px;
  color: #6B7280;
`;

const SourceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #9CA3AF;
  margin-top: 2px;
`;

const SourceDetail = styled.span`
  color: #6B7280;
  font-size: 11px;
`;

const CurrentBadge = styled.span`
  background-color: #D1FAE5;
  color: #059669;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 9999px;
`;

const ManagerOfRecordHistory = ({ employeeId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0
  });

  const fetchHistory = async (page = 1) => {
    if (!employeeId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await employeesApi.getManagerOfRecordHistory(employeeId, {
        page,
        limit: pagination.limit
      });
      setHistory(response.data || []);
      setPagination(prev => ({
        ...prev,
        page: response.pagination?.page || page,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err) {
      setError(err.message || 'Failed to load history');
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded && employeeId) {
      fetchHistory(1);
    }
  }, [isExpanded, employeeId]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePageChange = (_, newPage) => {
    fetchHistory(newPage);
  };

  // ADR-197: start/end are date-only — render the stored calendar day
  const formatDate = (dateString) => formatDateOnly(dateString, 'DD/MM/YYYY', 'Present');

  const getSourceDetail = (item) => {
    if (item.projectAtSource) {
      return item.projectAtSource.name;
    }
    if (item.orgUnitAtSource) {
      return item.orgUnitAtSource.name;
    }
    if (item.teamAtSource) {
      return item.teamAtSource.name;
    }
    return null;
  };

  const getSourceColor = (source) => {
    return MOR_SOURCE_COLORS[source] || '#6B7280';
  };

  return (
    <HistoryContainer>
      <HistoryHeader onClick={handleToggle}>
        <HistoryTitle>
          <FaHistory size={14} />
          Representative History
        </HistoryTitle>
        <IconButton size="small">
          {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </IconButton>
      </HistoryHeader>

      <Collapse in={isExpanded}>
        {isLoading ? (
          <Box sx={{ py: 2 }}>
            <LoadingSpinner size={24} message="Loading history..." />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : history.length === 0 ? (
          <EmptyState
            title="No Representative History"
            description="No representative history available for this employee."
          />
        ) : (
          <>
            <HistoryList>
              {history.map((item, index) => (
                <HistoryItem key={item.id}>
                  <TimelineContainer>
                    <TimelineDot $isCurrent={item.isCurrent} />
                    {index < history.length - 1 && <TimelineLine />}
                  </TimelineContainer>
                  <ManagerInfo>
                    <EmployeeAvatar employee={item.manager} size={32} />
                    <ManagerDetails>
                      <ManagerName>
                        {item.manager?.fullName || 'Unknown'}
                        {item.isCurrent && <CurrentBadge>Current</CurrentBadge>}
                      </ManagerName>
                      <DateRange>
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </DateRange>
                      <SourceInfo>
                        <Chip
                          label={MOR_SOURCE_LABELS[item.source] || item.source}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            backgroundColor: `${getSourceColor(item.source)}15`,
                            color: getSourceColor(item.source),
                            fontWeight: 500
                          }}
                        />
                        {getSourceDetail(item) && (
                          <>
                            <span>|</span>
                            <SourceDetail>{getSourceDetail(item)}</SourceDetail>
                          </>
                        )}
                      </SourceInfo>
                    </ManagerDetails>
                  </ManagerInfo>
                </HistoryItem>
              ))}
            </HistoryList>

            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  size="small"
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Collapse>
    </HistoryContainer>
  );
};

export default ManagerOfRecordHistory;
